<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, useId, watch } from "vue";
import LucideIcon from "../../components/icons/LucideIcon.vue";
import type {
  SortDirection,
  SuggestedPoSortField,
} from "./shippingPoGeneration.ts";
import type { ShippingPoLayout } from "./shippingPoSettings.ts";

const props = defineProps<{
  disabled: boolean;
  layout: ShippingPoLayout;
  sortField: SuggestedPoSortField;
  sortDirection: SortDirection;
}>();
const emit = defineEmits<{
  layout: [value: ShippingPoLayout];
  sort: [value: SuggestedPoSortField];
}>();
const fields: { label: string; value: SuggestedPoSortField }[] = [
  { label: "PO number", value: "poNumber" },
  { label: "Total PO cost", value: "totalCost" },
  { label: "Available capacity", value: "availableCapacity" },
  { label: "Arrival date", value: "arrivalDate" },
  { label: "Ship date", value: "shipDate" },
];
const menuId = useId();
const open = ref(false);
const root = ref<HTMLElement>();
const overlayHost = ref<HTMLElement>();
const trigger = ref<HTMLButtonElement>();
const menu = ref<HTMLElement>();

function close(restoreFocus = false): void {
  open.value = false;
  if (restoreFocus) trigger.value?.focus();
}

async function toggle(): Promise<void> {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    await nextTick();
    menu.value?.focus();
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (
    !open.value ||
    !(event.target instanceof Node) ||
    !root.value?.contains(event.target)
  )
    return;
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    close(true);
    return;
  }
}

function dismissOutside(event: PointerEvent): void {
  if (event.target instanceof Node && !root.value?.contains(event.target))
    close();
}

function dismissOnFocusout(event: FocusEvent): void {
  if (
    !(event.relatedTarget instanceof Node) ||
    !root.value?.contains(event.relatedTarget)
  )
    close();
}

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) close();
  }
);
onMounted(() => {
  document.addEventListener("pointerdown", dismissOutside);
  document.addEventListener("keydown", onKeydown);
});
onUnmounted(() => {
  document.removeEventListener("pointerdown", dismissOutside);
  document.removeEventListener("keydown", onKeydown);
});
</script>

<template>
  <div
    ref="root"
    role="group"
    aria-label="Suggested PO settings"
    class="shipping-po-settings"
    @focusout="dismissOnFocusout"
  >
    <button
      ref="trigger"
      type="button"
      class="shipping-po-settings-trigger"
      data-testid="shipping-po-settings-button"
      aria-label="Suggested PO settings"
      aria-haspopup="dialog"
      :aria-expanded="open"
      :aria-controls="open ? menuId : undefined"
      :disabled="disabled"
      @click="toggle"
      @keydown.down.prevent.stop="!open && toggle()"
    >
      <LucideIcon name="settings" :size="18" />
    </button>
    <div
      v-show="open"
      :id="menuId"
      ref="menu"
      role="dialog"
      tabindex="-1"
      aria-label="Suggested PO settings for all divisions"
      class="shipping-po-settings-menu"
      data-testid="shipping-po-settings-dialog"
    >
      <h4>Suggested PO settings</h4>
      <slot :overlay-target="overlayHost" />
      <div role="group" aria-label="Display">
        <p>Display · all divisions</p>
        <button
          v-for="option in [
            { label: 'Grid', value: 'grid' as const },
            { label: 'Horizontal', value: 'rail' as const },
          ]"
          :key="option.value"
          type="button"
          class="shipping-po-settings-choice"
          :aria-pressed="layout === option.value"
          :data-testid="`shipping-po-layout-${option.value}`"
          @click="emit('layout', option.value)"
        >
          {{ layout === option.value ? "✓ " : "" }}{{ option.label }}
        </button>
      </div>
      <div role="group" aria-label="Sort within each division">
        <p>Sort within each division</p>
        <button
          v-for="field in fields"
          :key="field.value"
          type="button"
          class="shipping-po-settings-choice"
          :aria-pressed="sortField === field.value"
          :aria-label="
            sortField === field.value
              ? `Sort by ${field.label}, ${sortDirection}; activate to reverse`
              : `Sort by ${field.label}`
          "
          :data-testid="`shipping-po-sort-${field.value}`"
          @click="emit('sort', field.value)"
        >
          {{ field.label }}
          <span v-if="sortField === field.value"
            >· {{ sortDirection }}
            {{ sortDirection === "ascending" ? "↑" : "↓" }}</span
          >
        </button>
      </div>
      <button
        type="button"
        class="shipping-po-settings-choice"
        data-testid="shipping-po-settings-close"
        @click="close(true)"
      >
        Close settings
      </button>
    </div>
    <div v-show="open" ref="overlayHost" />
  </div>
</template>
